import { useEffect, useState } from 'react'
import { useConnectionStore } from '@/shared/stores/connection.store'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useUIStore } from '@/shared/stores/ui.store'
import { useDebugPanelStore } from '@/shared/stores/debug-panel.store'
import { EnrollMachine } from '@/widgets/EnrollMachine'

type ConnectionStatus = 'online' | 'reconnecting' | 'offline'

interface TopBarProps {
  /** Callback pour ouvrir/fermer la sidebar en mode overlay mobile */
  onToggleSidebar?: () => void
  /** Callback pour créer une nouvelle session (mobile) */
  onCreateSession?: () => void
}

export function TopBar({ onToggleSidebar, onCreateSession }: TopBarProps = {}) {
  const connectionStatus = useConnectionStore(s => s.status)
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('theme') as 'dark' | 'light') ?? 'light',
  )
  const [fontSize, setFontSize] = useState<number>(
    () => Number(localStorage.getItem('fontSize') ?? '15'),
  )

  const user = useAuthStore(s => s.user)
  const isAdmin = user?.role === 'admin'
  const terminalMode = useUIStore(s => s.terminalMode)
  const toggleTerminalMode = useUIStore(s => s.toggleTerminalMode)
  const clearAuth = useAuthStore(s => s.clearAuth)
  const { open: openDebugPanel, debugEnabled, toggleDebug } = useDebugPanelStore()

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    document.documentElement.style.setProperty('--text-base', `${fontSize}px`)
    localStorage.setItem('fontSize', String(fontSize))
  }, [fontSize])

  const statusColor: Record<ConnectionStatus, string> = {
    online: 'var(--color-online)',
    reconnecting: 'var(--color-reconnecting)',
    offline: 'var(--color-offline)',
  }

  const handleLogout = () => {
    clearAuth()
    window.location.href = '/app/login'
  }

  return (
    <header
      className="topbar-header"
      style={{
        height: 96,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '0 var(--space-4)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-surface)',
        flexShrink: 0,
      }}
    >
      <img
        src="/app/logo-motherboard.png"
        alt="Motherboard"
        style={{
          height: 76,
          objectFit: 'contain',
          padding: '6px 0',
          marginRight: 12,
          filter: theme === 'dark' ? 'invert(1)' : 'none',
        }}
      />

      {/* Groupe header-line (+session, burger) — pousse a droite via margin-left:auto.
          Equivalent visuel exact de l'ancien justify-content:space-between (verifie),
          et se generalise proprement aux groupes ajoutes apres lui (topbar-row-1). */}
      <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginLeft: 'auto' }}>
        {/* Bouton nouvelle session mobile — visible uniquement en mobile via Sidebar.css */}
        {onCreateSession && (
          <button
            className="topbar-new-session"
            onClick={() => onCreateSession?.()}
            aria-label="Nouvelle conversation"
            title="Nouvelle conversation"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <line x1="10" y1="3" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}

        {/* Hamburger mobile — deplace en DERNIER (flush bord droit du header en mobile) — visible uniquement en mobile via Sidebar.css */}
        {onToggleSidebar && (
          <button
            className="topbar-hamburger"
            onClick={onToggleSidebar}
            aria-label="Ouvrir le menu sessions"
            title="Sessions"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="2" y="4" width="16" height="2" rx="1" fill="currentColor"/>
              <rect x="2" y="9" width="16" height="2" rx="1" fill="currentColor"/>
              <rect x="2" y="14" width="16" height="2" rx="1" fill="currentColor"/>
            </svg>
          </button>
        )}
      </div>

      {/* Ligne d'actions (mobile) — ENFANT DIRECT du header (pas nested dans .topbar-actions) :
          flex-basis:100% + header en flex-wrap:wrap force une ligne PLEINE LARGEUR du header
          (pas seulement la colonne a cote du logo). Inerte sur desktop (display:contents),
          ses enfants rejoignent alors le flux du header juste apres .topbar-actions. */}
      <div className="topbar-row topbar-row-1">
        {/* Terminal toggle (admin only) */}
        {isAdmin && (
          <button
            onClick={toggleTerminalMode}
            title={terminalMode ? 'Mode Discourse' : 'Mode Terminal'}
            className={`terminal-toggle ${terminalMode ? 'active' : ''}`}
          >
            {terminalMode ? 'DISC' : 'TERM'}
          </button>
        )}

        {/* Debug panel button (admin only) */}
        {isAdmin && (
          <button
            onClick={() => {
              if (!debugEnabled) toggleDebug()
              openDebugPanel()
            }}
            title="Ouvrir le panneau debug"
            style={{
              padding: '2px 8px',
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 3,
              color: debugEnabled ? 'var(--color-accent)' : 'var(--color-text-muted)',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
            }}
          >
            DBG
          </button>
        )}

        {/* Enrôler une machine (Iris/staxx) — tout utilisateur authentifié */}
        <EnrollMachine />

        {/* Font size (capsule AA) */}
        <button
          onClick={() => setFontSize(f => Math.max(11, f - 1))}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: 12,
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
            padding: '2px 4px',
          }}
          title="Reduire la taille du texte"
        >
          A-
        </button>
        <button
          onClick={() => setFontSize(f => Math.min(21, f + 1))}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: 15,
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
            padding: '2px 4px',
          }}
          title="Augmenter la taille du texte"
        >
          A+
        </button>

        {/* Theme toggle */}
        <button
          onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: 16,
            padding: '2px 4px',
          }}
          title={theme === 'dark' ? 'Mode jour' : 'Mode nuit'}
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>

        {/* Connection status */}
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: statusColor[connectionStatus],
            flexShrink: 0,
          }}
          title={connectionStatus}
        />
      </div>

      {/* Settings / logout — masque de la barre visible en mobile (className topbar-logout-inline,
          display:none dans le bloc @media mobile), l'action equivalente vit desormais dans le menu
          burger (footer de l'overlay Sidebar mobile). Reste visible et inchange sur desktop. */}
      <button
        className="topbar-logout-inline"
        onClick={handleLogout}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          fontSize: 'var(--text-xs)',
          fontFamily: 'var(--font-sans)',
          padding: '2px 4px',
          marginLeft: 'var(--space-1)',
        }}
        title="Se deconnecter"
      >
        Sortir
      </button>
    </header>
  )
}
